// frontend-react/src/components/league-dashboard/BoxScoreTile.js
// Yesterday's Box Scores - Individual Team Tile Component

import React, { useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { dynastyTheme } from '../../services/colorService';
import { formatBattingAvg, formatERA } from '../../services/statFormatUtils';
import PlayerLink from '../PlayerLink';

// Add custom scrollbar styles
const scrollbarStyles = `
  .dynasty-scrollbar::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  .dynasty-scrollbar::-webkit-scrollbar-track {
    background: #0a0a0a;
    border-radius: 4px;
  }
  .dynasty-scrollbar::-webkit-scrollbar-thumb {
    background: #404040;
    border-radius: 4px;
    border: 2px solid #0a0a0a;
  }
  .dynasty-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #525252;
  }
`;

// Inject styles once
if (typeof document !== 'undefined') {
  const styleId = 'dynasty-scrollbar-styles';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = scrollbarStyles;
    document.head.appendChild(style);
  }
}

/**
 * BoxScoreTile - Compact box score display for a single team
 * Shows top 5 hitters or pitchers with key stats
 * 
 * @param {string} teamId - Team UUID
 * @param {string} teamName - Team display name
 * @param {string} teamLogoUrl - Team logo URL
 * @param {Object} boxScoreData - Box score data from API
 * @param {boolean} loading - Loading state
 * @param {Object} error - Error object if fetch failed
 * @param {string} date - Date in YYYY-MM-DD format
 * @param {Function} onViewFull - Callback to navigate to full box score
 */
const BoxScoreTile = ({ 
  teamId, 
  teamName, 
  teamLogoUrl, 
  boxScoreData, 
  loading, 
  error, 
  date,
  onViewFull
}) => {
  const [activeTab, setActiveTab] = useState('hitting');

  // Loading skeleton
  if (loading) {
    return (
      <div className="flex-shrink-0 w-80">
        <div className={`${dynastyTheme.components.card.base} h-96 animate-pulse`}>
          <div className="p-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-neutral-700"></div>
              <div className="flex-1">
                <div className="h-4 bg-neutral-700 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-neutral-700 rounded w-1/2"></div>
              </div>
            </div>
            <div className="h-32 bg-neutral-700 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex-shrink-0 w-80">
        <div className={dynastyTheme.components.card.base}>
          <div className="p-4">
            <div className="flex items-center gap-3 mb-3">
              {teamLogoUrl ? (
                <>
                  <img 
                    src={teamLogoUrl} 
                    alt={teamName}
                    className="w-12 h-12 rounded object-cover border-2 border-neutral-700"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextElementSibling.style.display = 'flex';
                    }}
                  />
                  <div className="w-12 h-12 rounded bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center text-black font-bold text-sm" style={{ display: 'none' }}>
                    {teamName.substring(0, 2).toUpperCase()}
                  </div>
                </>
              ) : (
                <div className="w-12 h-12 rounded bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center text-black font-bold text-sm">
                  {teamName.substring(0, 2).toUpperCase()}
                </div>
              )}
              <div className="flex-1">
                <h4 className={`font-semibold ${dynastyTheme.classes.text.white}`}>{teamName}</h4>
                <p className={`text-xs ${dynastyTheme.classes.text.neutralLight}`}>{date}</p>
              </div>
            </div>
            <div className={`text-center py-12 ${dynastyTheme.classes.text.neutralLight}`}>
              <p className="text-sm">Unable to load box score</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // No data / No games played
  if (!boxScoreData || !boxScoreData.box_score) {
    return (
      <div className="flex-shrink-0 w-80">
        <div className={dynastyTheme.components.card.base}>
          <div className="p-4">
            <div className="flex items-center gap-3 mb-3">
              {teamLogoUrl ? (
                <>
                  <img 
                    src={teamLogoUrl} 
                    alt={teamName}
                    className="w-12 h-12 rounded object-cover border-2 border-neutral-700"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextElementSibling.style.display = 'flex';
                    }}
                  />
                  <div className="w-12 h-12 rounded bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center text-black font-bold text-sm" style={{ display: 'none' }}>
                    {teamName.substring(0, 2).toUpperCase()}
                  </div>
                </>
              ) : (
                <div className="w-12 h-12 rounded bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center text-black font-bold text-sm">
                  {teamName.substring(0, 2).toUpperCase()}
                </div>
              )}
              <div className="flex-1">
                <h4 className={`font-semibold ${dynastyTheme.classes.text.white}`}>{teamName}</h4>
                <p className={`text-xs ${dynastyTheme.classes.text.neutralLight}`}>{date}</p>
              </div>
            </div>
            <div className={`text-center py-12 ${dynastyTheme.classes.text.neutralLight}`}>
              <p className="text-sm">No games yesterday</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const { box_score } = boxScoreData;
  const { hitting = [], pitching = [], team_totals = {} } = box_score;

  // Calculate totals
  const hittingTotals = hitting.reduce((acc, player) => {
    const hitting = player.stats?.hitting || {};
    return {
      ab: (acc.ab || 0) + (hitting.at_bats || 0),
      r: (acc.r || 0) + (hitting.runs || 0),
      h: (acc.h || 0) + (hitting.hits || 0),
      hr: (acc.hr || 0) + (hitting.home_runs || 0),
      rbi: (acc.rbi || 0) + (hitting.rbi || 0),
      sb: (acc.sb || 0) + (hitting.stolen_bases || 0),
      bb: (acc.bb || 0) + (hitting.walks || 0),
      so: (acc.so || 0) + (hitting.strikeouts || 0)
    };
  }, {});

  const avg = hittingTotals.ab > 0 ? formatBattingAvg(hittingTotals.h / hittingTotals.ab) : '.000';
  // OPS calculation for totals - simplified, will use team_totals if available
  const ops = team_totals?.hitting?.ops != null
    ? formatBattingAvg(team_totals.hitting.ops)
    : '-';

  const pitchingTotals = pitching.reduce((acc, player) => {
    const pitching = player.stats?.pitching || {};
    return {
      w: (acc.w || 0) + (pitching.wins || 0),
      l: (acc.l || 0) + (pitching.losses || 0),
      sv: (acc.sv || 0) + (pitching.saves || 0),
      ip: (acc.ip || 0) + (pitching.innings_pitched || 0),
      h: (acc.h || 0) + (pitching.hits_allowed || 0),
      er: (acc.er || 0) + (pitching.earned_runs || 0),
      bb: (acc.bb || 0) + (pitching.walks_allowed || 0),
      so: (acc.so || 0) + (pitching.strikeouts_pitched || 0),
      qs: (acc.qs || 0) + (pitching.quality_starts || 0)
    };
  }, {});

  const era = pitchingTotals.ip > 0 ? formatERA((pitchingTotals.er * 9) / pitchingTotals.ip) : '0.00';
  const whip = pitchingTotals.ip > 0 ? formatBattingAvg((pitchingTotals.h + pitchingTotals.bb) / pitchingTotals.ip) : '.000';

  // Navigate to full box score
  const handleViewFull = () => {
    if (onViewFull) {
      onViewFull(teamId);
    }
  };

  return (
    <div className="flex-shrink-0 w-80">
      <div className={dynastyTheme.components.card.base}>
        <div className="p-4">
          {/* Header: Team Logo + Name - DRAGGABLE FOR TILE SCROLLING */}
          <div 
            className="flex items-center gap-3 mb-3 cursor-grab active:cursor-grabbing"
            onMouseDown={(e) => {
              // Enable drag on header for tile scrolling
              e.preventDefault();
              // Find the scroll container - it's 3 levels up from this tile
              const tile = e.currentTarget.closest('.flex-shrink-0');
              const scrollContainer = tile?.parentElement?.parentElement;
              
              if (scrollContainer && scrollContainer.classList.contains('overflow-x-auto')) {
                const startX = e.pageX;
                const scrollLeft = scrollContainer.scrollLeft;
                
                const handleMouseMove = (moveEvent) => {
                  const walk = (startX - moveEvent.pageX) * 2;
                  scrollContainer.scrollLeft = scrollLeft + walk;
                };
                
                const handleMouseUp = () => {
                  document.removeEventListener('mousemove', handleMouseMove);
                  document.removeEventListener('mouseup', handleMouseUp);
                };
                
                document.addEventListener('mousemove', handleMouseMove);
                document.addEventListener('mouseup', handleMouseUp);
              }
            }}
          >
            {teamLogoUrl ? (
              <>
                <img 
                  src={teamLogoUrl} 
                  alt={teamName}
                  className="w-12 h-12 rounded object-cover border-2 border-neutral-700"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextElementSibling.style.display = 'flex';
                  }}
                />
                <div className="w-12 h-12 rounded bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center text-black font-bold text-sm" style={{ display: 'none' }}>
                  {teamName.substring(0, 2).toUpperCase()}
                </div>
              </>
            ) : (
              <div className="w-12 h-12 rounded bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center text-black font-bold text-sm">
              {teamName.substring(0, 2).toUpperCase()}
              </div>
            )}
            <div className="flex-1">
              <h4 className={`font-semibold ${dynastyTheme.classes.text.white}`}>{teamName}</h4>
              <p className={`text-xs ${dynastyTheme.classes.text.neutralLight}`}>{date}</p>
            </div>
          </div>

          {/* Tabs: Hitters | Pitchers - Using sleek tab styling */}
          <div className={`flex border-b ${dynastyTheme.classes.border.neutral} mb-3`}>
            <button
              onClick={() => setActiveTab('hitting')}
              className={`flex-1 py-2 text-sm font-medium transition-colors ${
                activeTab === 'hitting'
                  ? `${dynastyTheme.classes.text.primary} border-b-2 border-yellow-400`
                  : `${dynastyTheme.classes.text.neutralLight} hover:${dynastyTheme.classes.text.white}`
              }`}
            >
              Hitters ({hitting.length})
            </button>
            <button
              onClick={() => setActiveTab('pitching')}
              className={`flex-1 py-2 text-sm font-medium transition-colors ${
                activeTab === 'pitching'
                  ? `${dynastyTheme.classes.text.primary} border-b-2 border-yellow-400`
                  : `${dynastyTheme.classes.text.neutralLight} hover:${dynastyTheme.classes.text.white}`
              }`}
            >
              Pitchers ({pitching.length})
            </button>
          </div>

          {/* Table Content - Using new mobile table format */}
          <div 
            style={{ 
              overflowX: 'auto',
              overflowY: 'hidden',
              WebkitOverflowScrolling: 'touch',
              marginTop: '8px',
              border: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
              borderRadius: '6px',
              isolation: 'isolate',
              margin: 0,
              padding: 0,
              width: '100%',
              position: 'relative',
              maxHeight: '400px'
            }}>
            <table style={{
              width: 'auto',
              minWidth: '100%',
              borderCollapse: 'collapse',
              fontSize: '9px',
              backgroundColor: dynastyTheme.tokens.colors.neutral[800],
              tableLayout: 'fixed',
              display: 'table'
            }}>
              <thead>
                <tr style={{
                  backgroundColor: dynastyTheme.tokens.colors.neutral[800],
                  borderBottom: `1px solid ${dynastyTheme.tokens.colors.neutral[600]}`
                }}>
                  {activeTab === 'hitting' ? (
                    <>
                      <th style={{ padding: '3px 0px', textAlign: 'center', position: 'sticky', left: 0, top: 0, backgroundColor: dynastyTheme.tokens.colors.neutral[800], zIndex: 11, width: '22px', minWidth: '22px', maxWidth: '22px', fontSize: '8px', color: dynastyTheme.tokens.colors.neutral[400], fontWeight: '600', textTransform: 'uppercase', opacity: 1, overflow: 'hidden', borderRight: 'none' }}>POS</th>
                      <th style={{ padding: '3px 0px', textAlign: 'left', position: 'sticky', left: 22, top: 0, backgroundColor: dynastyTheme.tokens.colors.neutral[800], zIndex: 10, width: '80px', minWidth: '80px', maxWidth: '80px', fontSize: '8px', color: dynastyTheme.tokens.colors.neutral[400], fontWeight: '600', textTransform: 'uppercase', opacity: 1, overflow: 'hidden' }}>Player</th>
                      <th style={{ padding: '3px 3px', textAlign: 'center', fontSize: '8px', color: dynastyTheme.tokens.colors.neutral[400], fontWeight: '600', width: '28px' }}>AB</th>
                      <th style={{ padding: '3px 3px', textAlign: 'center', fontSize: '8px', color: dynastyTheme.tokens.colors.neutral[400], fontWeight: '600', width: '24px' }}>R</th>
                      <th style={{ padding: '3px 3px', textAlign: 'center', fontSize: '8px', color: dynastyTheme.tokens.colors.neutral[400], fontWeight: '600', width: '24px' }}>H</th>
                      <th style={{ padding: '3px 3px', textAlign: 'center', fontSize: '8px', color: dynastyTheme.tokens.colors.neutral[400], fontWeight: '600', width: '28px' }}>HR</th>
                      <th style={{ padding: '3px 3px', textAlign: 'center', fontSize: '8px', color: dynastyTheme.tokens.colors.neutral[400], fontWeight: '600', width: '30px' }}>RBI</th>
                      <th style={{ padding: '3px 3px', textAlign: 'center', fontSize: '8px', color: dynastyTheme.tokens.colors.neutral[400], fontWeight: '600', width: '24px' }}>SB</th>
                      <th style={{ padding: '3px 3px', textAlign: 'center', fontSize: '8px', color: dynastyTheme.tokens.colors.neutral[400], fontWeight: '600', width: '24px' }}>BB</th>
                      <th style={{ padding: '3px 3px', textAlign: 'center', fontSize: '8px', color: dynastyTheme.tokens.colors.neutral[400], fontWeight: '600', width: '24px' }}>SO</th>
                      <th style={{ padding: '3px 3px', textAlign: 'center', fontSize: '8px', color: dynastyTheme.tokens.colors.neutral[400], fontWeight: '600', width: '36px' }}>AVG</th>
                      <th style={{ padding: '3px 3px', textAlign: 'center', fontSize: '8px', color: dynastyTheme.tokens.colors.neutral[400], fontWeight: '600', width: '36px' }}>OPS</th>
                    </>
                  ) : (
                    <>
                      <th style={{ padding: '3px 0px', textAlign: 'center', position: 'sticky', left: 0, top: 0, backgroundColor: dynastyTheme.tokens.colors.neutral[800], zIndex: 11, width: '22px', minWidth: '22px', maxWidth: '22px', fontSize: '8px', color: dynastyTheme.tokens.colors.neutral[400], fontWeight: '600', textTransform: 'uppercase', opacity: 1, overflow: 'hidden', borderRight: 'none' }}>POS</th>
                      <th style={{ padding: '3px 0px', textAlign: 'left', position: 'sticky', left: 22, top: 0, backgroundColor: dynastyTheme.tokens.colors.neutral[800], zIndex: 10, width: '80px', minWidth: '80px', maxWidth: '80px', fontSize: '8px', color: dynastyTheme.tokens.colors.neutral[400], fontWeight: '600', textTransform: 'uppercase', opacity: 1, overflow: 'hidden' }}>Player</th>
                      <th style={{ padding: '3px 3px', textAlign: 'center', fontSize: '8px', color: dynastyTheme.tokens.colors.neutral[400], fontWeight: '600', width: '22px' }}>W</th>
                      <th style={{ padding: '3px 3px', textAlign: 'center', fontSize: '8px', color: dynastyTheme.tokens.colors.neutral[400], fontWeight: '600', width: '22px' }}>L</th>
                      <th style={{ padding: '3px 3px', textAlign: 'center', fontSize: '8px', color: dynastyTheme.tokens.colors.neutral[400], fontWeight: '600', width: '24px' }}>SV</th>
                      <th style={{ padding: '3px 3px', textAlign: 'center', fontSize: '8px', color: dynastyTheme.tokens.colors.neutral[400], fontWeight: '600', width: '28px' }}>IP</th>
                      <th style={{ padding: '3px 3px', textAlign: 'center', fontSize: '8px', color: dynastyTheme.tokens.colors.neutral[400], fontWeight: '600', width: '22px' }}>H</th>
                      <th style={{ padding: '3px 3px', textAlign: 'center', fontSize: '8px', color: dynastyTheme.tokens.colors.neutral[400], fontWeight: '600', width: '24px' }}>ER</th>
                      <th style={{ padding: '3px 3px', textAlign: 'center', fontSize: '8px', color: dynastyTheme.tokens.colors.neutral[400], fontWeight: '600', width: '24px' }}>BB</th>
                      <th style={{ padding: '3px 3px', textAlign: 'center', fontSize: '8px', color: dynastyTheme.tokens.colors.neutral[400], fontWeight: '600', width: '24px' }}>SO</th>
                      <th style={{ padding: '3px 3px', textAlign: 'center', fontSize: '8px', color: dynastyTheme.tokens.colors.neutral[400], fontWeight: '600', width: '24px' }}>QS</th>
                      <th style={{ padding: '3px 3px', textAlign: 'center', fontSize: '8px', color: dynastyTheme.tokens.colors.neutral[400], fontWeight: '600', width: '34px' }}>ERA</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {(activeTab === 'hitting' ? hitting : pitching).map((player, idx) => {
                  const stats = activeTab === 'hitting' ? player.stats?.hitting || {} : player.stats?.pitching || {};
                  const rowBgColor = idx % 2 === 0 ? '#1a1a1a' : dynastyTheme.tokens.colors.neutral[800];

                  const lineupPosition = player.lineup_position || '-';
                  const displayPosition = lineupPosition.includes('_')
                    ? lineupPosition.split('_')[0]
                    : lineupPosition;
  
                  return (
                    <tr key={idx} style={{
                      backgroundColor: rowBgColor,
                      borderBottom: `1px solid ${dynastyTheme.tokens.colors.neutral[800]}`,
                      height: '26px',
                      minHeight: '26px',
                      maxHeight: '26px',
                      lineHeight: '1.0'
                    }}>
                      {/* POS Column (Sticky) */}
                      <td 
                        style={{ 
                          padding: '0px',
                          position: 'sticky', 
                          left: 0, 
                          backgroundColor: rowBgColor, 
                          zIndex: 11, 
                          width: '22px', 
                          minWidth: '22px', 
                          maxWidth: '22px',
                          height: '26px',
                          minHeight: '26px',
                          maxHeight: '26px',
                          opacity: 1,
                          overflow: 'hidden',
                          borderRight: 'none',
                          display: 'table-cell'
                        }}
                      >
                        <div style={{
                          backgroundColor: rowBgColor,
                          width: '100%',
                          height: '26px',
                          minHeight: '26px',
                          maxHeight: '26px',
                          margin: 0,
                          paddingTop: '1px',
                          paddingBottom: '1px',
                          paddingLeft: 0,
                          paddingRight: 0,
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          zIndex: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'hidden',
                          fontSize: '9px',
                          color: dynastyTheme.tokens.colors.warning,
                          fontWeight: '700'
                        }}>
                          {displayPosition}
                        </div>
                      </td>
                      {/* Player Column (Sticky) */}
                      <td 
                        style={{ 
                          padding: '2px 4px',
                          position: 'sticky', 
                          left: 22,
                          top: 0,
                          backgroundColor: rowBgColor, 
                          zIndex: 10, 
                          width: '80px', 
                          minWidth: '80px', 
                          maxWidth: '80px',
                          height: '26px',
                          minHeight: '26px',
                          maxHeight: '26px',
                          opacity: 1,
                          boxShadow: '2px 0 4px rgba(0, 0, 0, 0.3)',
                          overflow: 'hidden',
                          borderRight: 'none',
                          display: 'table-cell',
                          whiteSpace: 'nowrap',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        <PlayerLink mlbId={player.player_id} name={player.player_name} style={{ color: '#fff', fontSize: '11px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} />
                      </td>
                      {activeTab === 'hitting' ? (
                        <>
                          <td style={{ padding: '2px 3px', fontSize: '11px', color: '#fff', textAlign: 'center', height: '26px', minHeight: '26px' }}>{stats.at_bats || 0}</td>
                          <td style={{ padding: '2px 3px', fontSize: '11px', color: '#fff', textAlign: 'center', height: '26px', minHeight: '26px' }}>{stats.runs || 0}</td>
                          <td style={{ padding: '2px 3px', fontSize: '11px', color: '#fff', textAlign: 'center', height: '26px', minHeight: '26px' }}>{stats.hits || 0}</td>
                          <td style={{ padding: '2px 3px', fontSize: '11px', color: '#fff', textAlign: 'center', height: '26px', minHeight: '26px' }}>{stats.home_runs || 0}</td>
                          <td style={{ padding: '2px 3px', fontSize: '11px', color: '#fff', textAlign: 'center', height: '26px', minHeight: '26px' }}>{stats.rbi || 0}</td>
                          <td style={{ padding: '2px 3px', fontSize: '11px', color: '#fff', textAlign: 'center', height: '26px', minHeight: '26px' }}>{stats.stolen_bases || 0}</td>
                          <td style={{ padding: '2px 3px', fontSize: '11px', color: '#fff', textAlign: 'center', height: '26px', minHeight: '26px' }}>{stats.walks || 0}</td>
                          <td style={{ padding: '2px 3px', fontSize: '11px', color: '#fff', textAlign: 'center', height: '26px', minHeight: '26px' }}>{stats.strikeouts || 0}</td>
                          <td style={{ padding: '2px 3px', fontSize: '11px', color: '#fff', textAlign: 'center', height: '26px', minHeight: '26px' }}>{formatBattingAvg(stats.batting_avg)}</td>
                          <td style={{ padding: '2px 3px', fontSize: '11px', color: '#fff', textAlign: 'center', height: '26px', minHeight: '26px' }}>{formatBattingAvg(stats.ops)}</td>
                        </>
                      ) : (
                        <>
                          <td style={{ padding: '2px 3px', fontSize: '11px', color: '#fff', textAlign: 'center', height: '26px', minHeight: '26px' }}>{stats.wins || 0}</td>
                          <td style={{ padding: '2px 3px', fontSize: '11px', color: '#fff', textAlign: 'center', height: '26px', minHeight: '26px' }}>{stats.losses || 0}</td>
                          <td style={{ padding: '2px 3px', fontSize: '11px', color: '#fff', textAlign: 'center', height: '26px', minHeight: '26px' }}>{stats.saves || 0}</td>
                          <td style={{ padding: '2px 3px', fontSize: '11px', color: '#fff', textAlign: 'center', height: '26px', minHeight: '26px' }}>{stats.innings_pitched?.toFixed(1) || '0.0'}</td>
                          <td style={{ padding: '2px 3px', fontSize: '11px', color: '#fff', textAlign: 'center', height: '26px', minHeight: '26px' }}>{stats.hits_allowed || 0}</td>
                          <td style={{ padding: '2px 3px', fontSize: '11px', color: '#fff', textAlign: 'center', height: '26px', minHeight: '26px' }}>{stats.earned_runs || 0}</td>
                          <td style={{ padding: '2px 3px', fontSize: '11px', color: '#fff', textAlign: 'center', height: '26px', minHeight: '26px' }}>{stats.walks_allowed || 0}</td>
                          <td style={{ padding: '2px 3px', fontSize: '11px', color: '#fff', textAlign: 'center', height: '26px', minHeight: '26px' }}>{stats.strikeouts || 0}</td>
                          <td style={{ padding: '2px 3px', fontSize: '11px', color: '#fff', textAlign: 'center', height: '26px', minHeight: '26px' }}>{stats.quality_starts || 0}</td>
                          <td style={{ padding: '2px 3px', fontSize: '11px', color: '#fff', textAlign: 'center', height: '26px', minHeight: '26px' }}>{formatERA(stats.era)}</td>
                        </>
                      )}
                    </tr>
                  );
                })}
                
                {/* Team Totals Row */}
                {(activeTab === 'hitting' ? hitting : pitching).length > 0 && (
                  <tr style={{
                    backgroundColor: '#262626',
                    borderTop: `2px solid ${dynastyTheme.tokens.colors.neutral[600]}`,
                    fontWeight: 'bold'
                  }}>
                    <td style={{ padding: '4px 0px', fontSize: '10px', color: dynastyTheme.tokens.colors.neutral[300], fontWeight: '700', position: 'sticky', left: 0, backgroundColor: '#262626', zIndex: 11, width: '22px', minWidth: '22px', maxWidth: '22px', textAlign: 'center', opacity: 1, overflow: 'hidden', borderRight: 'none' }}>
                      -
                    </td>
                    <td style={{ padding: '4px 0px', fontSize: '10px', color: dynastyTheme.tokens.colors.neutral[300], fontWeight: '700', position: 'sticky', left: 22, backgroundColor: '#262626', zIndex: 10, width: '80px', minWidth: '80px', maxWidth: '80px', opacity: 1, overflow: 'hidden' }}>
                      TEAM TOTALS
                    </td>
                    {activeTab === 'hitting' ? (
                      <>
                        <td style={{ padding: '4px 3px', fontSize: '10px', color: dynastyTheme.tokens.colors.neutral[200], textAlign: 'center', fontWeight: '700' }}>{hittingTotals.ab}</td>
                        <td style={{ padding: '4px 3px', fontSize: '10px', color: dynastyTheme.tokens.colors.neutral[200], textAlign: 'center', fontWeight: '700' }}>{hittingTotals.r}</td>
                        <td style={{ padding: '4px 3px', fontSize: '10px', color: dynastyTheme.tokens.colors.neutral[200], textAlign: 'center', fontWeight: '700' }}>{hittingTotals.h}</td>
                        <td style={{ padding: '4px 3px', fontSize: '10px', color: dynastyTheme.tokens.colors.neutral[200], textAlign: 'center', fontWeight: '700' }}>{hittingTotals.hr}</td>
                        <td style={{ padding: '4px 3px', fontSize: '10px', color: dynastyTheme.tokens.colors.neutral[200], textAlign: 'center', fontWeight: '700' }}>{hittingTotals.rbi}</td>
                        <td style={{ padding: '4px 3px', fontSize: '10px', color: dynastyTheme.tokens.colors.neutral[200], textAlign: 'center', fontWeight: '700' }}>{hittingTotals.sb}</td>
                        <td style={{ padding: '4px 3px', fontSize: '10px', color: dynastyTheme.tokens.colors.neutral[200], textAlign: 'center', fontWeight: '700' }}>{hittingTotals.bb}</td>
                        <td style={{ padding: '4px 3px', fontSize: '10px', color: dynastyTheme.tokens.colors.neutral[200], textAlign: 'center', fontWeight: '700' }}>{hittingTotals.so}</td>
                        <td style={{ padding: '4px 3px', fontSize: '10px', color: dynastyTheme.tokens.colors.neutral[200], textAlign: 'center', fontWeight: '700' }}>{avg}</td>
                        <td style={{ padding: '4px 3px', fontSize: '10px', color: dynastyTheme.tokens.colors.neutral[400], textAlign: 'center', fontWeight: '700' }}>-</td>
                      </>
                    ) : (
                      <>
                        <td style={{ padding: '4px 3px', fontSize: '10px', color: dynastyTheme.tokens.colors.neutral[200], textAlign: 'center', fontWeight: '700' }}>{pitchingTotals.w}</td>
                        <td style={{ padding: '4px 3px', fontSize: '10px', color: dynastyTheme.tokens.colors.neutral[200], textAlign: 'center', fontWeight: '700' }}>{pitchingTotals.l}</td>
                        <td style={{ padding: '4px 3px', fontSize: '10px', color: dynastyTheme.tokens.colors.neutral[200], textAlign: 'center', fontWeight: '700' }}>{pitchingTotals.sv}</td>
                        <td style={{ padding: '4px 3px', fontSize: '10px', color: dynastyTheme.tokens.colors.neutral[200], textAlign: 'center', fontWeight: '700' }}>{pitchingTotals.ip.toFixed(1)}</td>
                        <td style={{ padding: '4px 3px', fontSize: '10px', color: dynastyTheme.tokens.colors.neutral[200], textAlign: 'center', fontWeight: '700' }}>{pitchingTotals.h}</td>
                        <td style={{ padding: '4px 3px', fontSize: '10px', color: dynastyTheme.tokens.colors.neutral[200], textAlign: 'center', fontWeight: '700' }}>{pitchingTotals.er}</td>
                        <td style={{ padding: '4px 3px', fontSize: '10px', color: dynastyTheme.tokens.colors.neutral[200], textAlign: 'center', fontWeight: '700' }}>{pitchingTotals.bb}</td>
                        <td style={{ padding: '4px 3px', fontSize: '10px', color: dynastyTheme.tokens.colors.neutral[200], textAlign: 'center', fontWeight: '700' }}>{pitchingTotals.so}</td>
                        <td style={{ padding: '4px 3px', fontSize: '10px', color: dynastyTheme.tokens.colors.neutral[200], textAlign: 'center', fontWeight: '700' }}>{pitchingTotals.qs}</td>
                        <td style={{ padding: '4px 3px', fontSize: '10px', color: dynastyTheme.tokens.colors.neutral[200], textAlign: 'center', fontWeight: '700' }}>{era}</td>
                      </>
                    )}
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Footer: View Full Link */}
          <div className={`mt-3 pt-3 border-t ${dynastyTheme.classes.border.neutral}`}>
            <button 
              onClick={handleViewFull}
              className={`text-xs ${dynastyTheme.classes.text.primary} hover:underline flex items-center gap-1 transition-colors`}
            >
              View Full Box Score
              <ArrowRight className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BoxScoreTile;
